import React from 'react';
import {
    Stack,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
} from '@mui/material';
import { PageTitle } from 'components/shared/page_title';
import { useLocation } from 'react-router-dom';

interface Produto {
    id: string;
    nomeRazao: string;
    documento: string;
    valor: string;
    valorNumerico: number;
    fonte: 'federal' | 'estadual' | 'municipal';
    tipoFornecedor: 'CNPJ' | 'CPF';
}

interface SecaoAquisicao {
    titulo: string;
    produtosCnpj: Produto[];
    produtosCpf: Produto[];
}

interface ViewSchoolFeedingAcquisitionsProps {
    secoes: SecaoAquisicao[];
}

const formatarValorParaExibicao = (valor: number | string | null | undefined) => {
    if (!valor && valor !== 0) return '';
    const numeroValor = typeof valor === 'string' ? parseFloat(valor.replace(/\./g, '').replace(',', '.')) : valor;
    if (isNaN(numeroValor)) return '';
    return numeroValor.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const FONTES = [
    { key: 'federal', label: 'Federal (PNAE)' },
    { key: 'estadual', label: 'Estadual' },
    { key: 'municipal', label: 'Municipal' },
];

const ViewSchoolFeedingAcquisitions: React.FC<ViewSchoolFeedingAcquisitionsProps> = ({ secoes }) => {
    const location = useLocation();
    // Extrai o ano da rota, ex: /service_management/cifa-2027 => 2027
    const match = /cifa-(\d{4})/.exec(location.pathname);
    const anoReferencia = match ? Number(match[1]) : 2026;

    // Função para gerar key única para seções
    const getSecaoKey = (secao: SecaoAquisicao, index: number) => {
        return secao.titulo.replace(/\s+/g, '-').toLowerCase() || `secao-${index}`;
    };

    return (
        <>
            <PageTitle
                title={`Aquisição de alimentação escolar - ${anoReferencia}`}
                subtitle={`Visualização das aquisições organizadas por fonte de recurso e tipo de fornecedor em ${anoReferencia}.`}
            />

            <Stack marginX="4rem" spacing="2rem" maxWidth="1152px" width="100%">
                {secoes.map((secao, secaoIndex) => (
                    <Paper key={getSecaoKey(secao, secaoIndex)} sx={{ p: 3 }}>
                        <Typography variant="h6" fontWeight={600} mb={2}>
                            {secao.titulo}
                        </Typography>
                        {FONTES.map(fonte => {
                            // Agrupa produtos por fonte e tipo de fornecedor
                            const produtosCnpj = secao.produtosCnpj.filter(p => p.fonte === fonte.key);
                            const produtosCpf = secao.produtosCpf.filter(p => p.fonte === fonte.key);

                            if (produtosCnpj.length === 0 && produtosCpf.length === 0) return null;

                            return (
                                <Box key={fonte.key} mb={3}>
                                    <Typography variant="subtitle1" fontWeight={500} mb={1}>
                                        Fonte de recurso: {fonte.label}
                                    </Typography>
                                    {/* CNPJ */}
                                    {produtosCnpj.length > 0 && (
                                        <>
                                            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                                                Fornecedor: CNPJ
                                            </Typography>
                                            <TableContainer component={Paper} sx={{ mb: 2 }}>
                                                <Table>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Nome/Razão Social</TableCell>
                                                            <TableCell>CNPJ</TableCell>
                                                            <TableCell>Valor (R$)</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {produtosCnpj.map(produto => (
                                                            <TableRow key={produto.id}>
                                                                <TableCell>{produto.nomeRazao}</TableCell>
                                                                <TableCell>{produto.documento}</TableCell>
                                                                <TableCell>R$ {formatarValorParaExibicao(produto.valor)}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </>
                                    )}
                                    {/* CPF */}
                                    {produtosCpf.length > 0 && (
                                        <>
                                            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                                                Fornecedor: CPF
                                            </Typography>
                                            <TableContainer component={Paper}>
                                                <Table>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Nome/Razão Social</TableCell>
                                                            <TableCell>CPF</TableCell>
                                                            <TableCell>Valor (R$)</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {produtosCpf.map(produto => (
                                                            <TableRow key={produto.id}>
                                                                <TableCell>{produto.nomeRazao}</TableCell>
                                                                <TableCell>{produto.documento}</TableCell>
                                                                <TableCell>R$ {formatarValorParaExibicao(produto.valor)}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </>
                                    )}
                                </Box>
                            );
                        })}
                    </Paper>
                ))}
            </Stack>
        </>
    );
};

export default ViewSchoolFeedingAcquisitions;
